// src/components/AutomationToolPicker.jsx
import React from "react";
import ToolIcon from "./ToolIcon";

// An option may carry `icons` (ToolIcon keys, e.g. a person's calendar
// providers), drawn after its label; the label stays the text.
function OptionIcons({ icons }) {
  if (!Array.isArray(icons) || !icons.length) return null;
  return (
    <span className="selectIcons" aria-hidden="true">
      {icons.map((k) => (
        <ToolIcon key={k} tool={k} />
      ))}
    </span>
  );
}

// Also used on its own by the automation cards' tool pickers
// (components/ToolChoiceField.js).
export function ToolDropdown({
  value,
  options,
  onChange,
  placeholder = "Select…",
  ariaLabel,
}) {
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef(null);

  React.useEffect(() => {
    function onDoc(e) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const selected =
    options.find((o) => String(o.value) === String(value)) || null;

  return (
    <div
      className={`select ${open ? "open" : ""}`}
      ref={wrapRef}
      style={{ position: "relative" }}
    >
      <button
        type="button"
        className="selectTrigger"
        onClick={() => setOpen((v) => !v)}
        style={{ width: "100%" }}
        // The value is clipped with an ellipsis when it does not fit its column.
        title={selected ? selected.label : placeholder}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        <span className="selectValue">
          {selected ? selected.label : placeholder}
        </span>
        {selected ? <OptionIcons icons={selected.icons} /> : null}
        <span className="selectCaret" aria-hidden />
      </button>

      <div
        className="selectPanel"
        role="listbox"
        style={{ maxHeight: 280, overflow: "auto" }}
      >
        <div className="scrollable" style={{ padding: 6 }}>
          {options.map((opt) => {
            const disabled = !!opt.disabled;
            const isSel = String(opt.value) === String(value);

            return (
              <div
                key={opt.value}
                role="option"
                aria-selected={isSel}
                aria-disabled={disabled || undefined}
                className={`selectOption ${isSel ? "selected" : ""}`}
                onClick={() => {
                  if (disabled) return;
                  onChange?.(opt.value);
                  setOpen(false);
                }}
                style={{
                  opacity: disabled ? 0.5 : 1,
                  cursor: disabled ? "not-allowed" : "pointer",
                }}
                title={disabled ? opt.reason || "Not connected" : ""}
              >
                {opt.label}
                <OptionIcons icons={opt.icons} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function AutomationToolPicker({
  label,
  help,
  value,
  onChange,
  options = [], // [{ value, label, disabled?, reason?, icons? }]
  placeholder,
}) {
  return (
    // Label, control, then help — in that order. With help above the control,
    // a two-line help in one column pushed its dropdown a line lower than its
    // neighbours', so nothing lined up across a row of pickers. .crField then
    // keeps the rows at their natural height: these cells get stretched by the
    // row, and stretched auto rows grow the control itself.
    <div className="crField">
      {label ? <div className="crFieldLabel">{label}</div> : null}

      <ToolDropdown
        value={value}
        options={options}
        onChange={onChange}
        placeholder={placeholder}
      />

      {help ? <div className="muted small">{help}</div> : null}
    </div>
  );
}
